/**
 * BookProgress — «статус по этапам» в личном кабинете.
 *
 * Показывается после того, как юзер завершил книгу: слева вертикальная линия
 * с маркерами, справа — описание этапа и плашка со сроком. Пройденные этапы
 * с галочкой, текущий мигает без галочки, будущие серые.
 *
 * Все даты и состояния считает бэк (GET /orders/my/progress,
 * services/orderProgress.ts) — здесь только отрисовка и локализация.
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import infoIcon from '../../assets/info-hint.svg';
import './BookProgress.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

type StageKey =
  | 'filling'
  | 'editing'
  | 'approval'
  | 'postpayment'
  | 'printing'
  | 'packing'
  | 'handover'
  | 'delivery';

type StageState = 'done' | 'current' | 'pending';

interface Stage {
  key: StageKey;
  state: StageState;
  date: string | null;
  days?: number;
}

interface Progress {
  status: string;
  finishedAt: string | null;
  stages: Stage[];
  payment: {
    paid: number;
    fullAmount: number;
    remainder: number;
    currency: string;
  } | null;
  deliveryFromCdek: boolean;
}

/**
 * Локаль для дат. Если в движке нет данных языка (в части браузеров так с
 * ky/az/be), Intl молча скатывается на root-локаль и печатает «M09 18»
 * вместо названия месяца. Ловим это двумя проверками — по resolvedOptions
 * и по самому результату — и откатываемся на русский: он понятнее
 * английского для всех наших рынков.
 */
const DATE_FALLBACK = 'ru';
const ROOT_MONTH = /M\d{2}/;

function dateLocale(lang: string | undefined): string {
  const base = (lang || DATE_FALLBACK).split('-')[0];
  try {
    const resolved = new Intl.DateTimeFormat(base).resolvedOptions().locale;
    if (resolved.split('-')[0] !== base) return DATE_FALLBACK;
    const probe = new Intl.DateTimeFormat(base, {
      day: 'numeric',
      month: 'long',
    }).format(new Date(2024, 8, 18));
    if (ROOT_MONTH.test(probe)) return DATE_FALLBACK;
    return base;
  } catch {
    return DATE_FALLBACK;
  }
}

// Этапы со знаком «i» справа от плашки: по клику разворачивается пояснение.
const HINT_STAGES: StageKey[] = ['editing', 'printing'];

const CheckIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M5 13l4 4L19 7" />
  </svg>
);

const PIN_MIN_SIZE = 8;

/**
 * Плашка со сроком. Всегда в одну строку — перенос запрещён.
 *
 * Размер шрифта у всех плашек одинаковый: каждая сама считает, какой
 * максимум ей влезает в остаток строки, отдаёт его наверх через onMeasure,
 * а BookProgress берёт минимум по всем и возвращает его обратно пропом
 * `size`. Так самая длинная плашка задаёт размер для остальных.
 */
const Pin: React.FC<{
  stageKey: StageKey;
  label: string;
  value?: string;
  size: number | null;
  onMeasure: (key: StageKey, size: number) => void;
}> = ({ stageKey, label, value, size, onMeasure }) => {
  const ref = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    const row = el?.parentElement;
    if (!el || !row) return;

    const measure = () => {
      // Меряем всегда от базового размера, а не от уже применённого общего:
      // иначе размер полз бы вниз с каждым пересчётом.
      const applied = el.style.fontSize;
      el.style.fontSize = '';
      const base = parseFloat(getComputedStyle(el).fontSize);
      if (!base) {
        el.style.fontSize = applied;
        return;
      }
      const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
      // Остаток строки = её ширина минус всё, что стоит рядом с плашкой.
      const taken = Array.from(row.children)
        .filter((c) => c !== el && !(c as HTMLElement).dataset.overlay)
        .reduce((sum, c) => sum + (c as HTMLElement).offsetWidth + gap, 0);
      const available = row.clientWidth - taken;
      if (available <= 0) {
        el.style.fontSize = applied;
        return;
      }
      // offsetWidth, а не scrollWidth: плашке запрещено сжиматься
      // (flex-shrink: 0), поэтому её ширина равна ширине содержимого, и
      // переполнение видно напрямую. scrollWidth при overflow: visible
      // ведёт себя по-разному в движках.
      let fits = base;
      while (el.offsetWidth > available + 0.5 && fits > PIN_MIN_SIZE) {
        fits -= 0.5;
        el.style.fontSize = `${fits}px`;
      }
      el.style.fontSize = applied;
      onMeasure(stageKey, fits);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(row);
    return () => ro.disconnect();
  }, [stageKey, label, value, onMeasure]);

  return (
    <span
      ref={ref}
      className="mg-prg__pin"
      style={size ? { fontSize: size } : undefined}
    >
      <span className="mg-prg__pin-label">{label}</span>
      {value && <b className="mg-prg__pin-value">{value}</b>}
    </span>
  );
};

/**
 * Строка «плашка + знак i» и подсказка под ней. Подсказка абсолютная и
 * растёт от иконки влево: её правый край равен правому краю кнопки, левый
 * упирается в начало строки. В CSS эту точку не выразить — позиция зависит
 * от ширины плашки, а та ещё и ужимается шрифтом.
 */
const PinRow: React.FC<{
  label: string;
  value?: string;
  hint?: string;
  open: boolean;
  onToggle: () => void;
  stageKey: StageKey;
  pinSize: number | null;
  onPinMeasure: (key: StageKey, size: number) => void;
}> = ({ label, value, hint, open, onToggle, stageKey, pinSize, onPinMeasure }) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLButtonElement>(null);
  const [hintRight, setHintRight] = useState(0);

  useLayoutEffect(() => {
    if (!open) return;
    const sync = () => {
      const icon = iconRef.current;
      const row = rowRef.current;
      if (!icon || !row) return;
      // Отступ справа = сколько остаётся от правого края иконки до конца строки.
      setHintRight(row.clientWidth - (icon.offsetLeft + icon.offsetWidth));
    };
    sync();
    const row = rowRef.current;
    if (!row) return;
    const ro = new ResizeObserver(sync);
    ro.observe(row);
    return () => ro.disconnect();
  }, [open, label, value]);

  return (
    <div className="mg-prg__pin-row" ref={rowRef}>
      <Pin
        stageKey={stageKey}
        label={label}
        value={value}
        size={pinSize}
        onMeasure={onPinMeasure}
      />
      {hint && (
        <button
          ref={iconRef}
          type="button"
          className="mg-prg__info"
          aria-expanded={open}
          onClick={onToggle}
        >
          <img src={infoIcon} alt="" width={18} height={18} />
        </button>
      )}
      {hint && open && (
        // data-overlay: подсказка вынута из потока, в расчёте свободного
        // места под плашку она не участвует.
        <div className="mg-prg__hint" data-overlay="1" style={{ right: hintRight }}>
          {hint}
        </div>
      )}
    </div>
  );
};

const BookProgress: React.FC = () => {
  const { t, i18n } = useTranslation('dashboard');
  const [data, setData] = useState<Progress | null>(null);
  const [openHint, setOpenHint] = useState<StageKey | null>(null);
  // Размер шрифта плашек — один на всех, по самой тесной (см. Pin).
  const pinSizes = useRef<Partial<Record<StageKey, number>>>({});
  const [pinSize, setPinSize] = useState<number | null>(null);

  const handlePinMeasure = useCallback((key: StageKey, size: number) => {
    if (pinSizes.current[key] === size) return;
    pinSizes.current[key] = size;
    const min = Math.min(...Object.values(pinSizes.current as Record<string, number>));
    setPinSize((prev) => (prev === min ? prev : min));
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/orders/my/progress`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setData(d);
      } catch (err) {
        console.error('failed to fetch book progress', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!data || !data.stages?.length) return null;

  const lang = dateLocale(i18n.language);

  // «16 сентября» — день и месяц без года: все сроки в пределах пары недель.
  const fmtDate = (iso: string | null): string | null => {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return new Intl.DateTimeFormat(lang, { day: 'numeric', month: 'long' }).format(d);
  };

  const fmtMoney = (v: number, currency: string) =>
    `${v.toLocaleString(lang)} ${currency}`;

  /** Срок прописью. Множественное число выбираем сами: значений всего 1–3,
   *  и так не зависим от того, как в проекте настроены плюралы i18next. */
  const daysPhrase = (days: number, kind: 'calendar' | 'working') =>
    t(`progress_days_${kind}_${days === 1 ? 'one' : 'few'}`, { count: days });

  /** Плашка под текстом: серая часть — подпись, жирная — значение. */
  const pinFor = (stage: Stage): { label: string; value?: string } | null => {
    const date = fmtDate(stage.date);
    switch (stage.key) {
      case 'filling':
        return date ? { label: t('progress_filling_pin'), value: date } : null;
      case 'editing':
        return {
          label: `${daysPhrase(stage.days ?? 3, 'calendar')}. ${t('progress_editing_pin')}`,
          value: date ?? undefined,
        };
      case 'approval':
        // Срок утверждения по-прежнему участвует в расчёте дат печати,
        // но в плашке его не показываем — там про сроки правок.
        return { label: t('progress_approval_pin') };
      case 'postpayment': {
        if (!data.payment) return null;
        const { paid, fullAmount, remainder, currency } = data.payment;
        return {
          label: t('progress_postpayment_pin', {
            paid: fmtMoney(paid, currency),
            full: fmtMoney(fullAmount, currency),
          }),
          value: fmtMoney(remainder, currency),
        };
      }
      case 'printing':
        return {
          label: `${daysPhrase(stage.days ?? 2, 'working')}. ${t('progress_ready_pin')}`,
          value: date ?? undefined,
        };
      case 'packing':
        return { label: t('progress_packing_pin'), value: date ?? undefined };
      case 'handover':
        return { label: t('progress_handover_pin'), value: date ?? undefined };
      case 'delivery':
        // Дату знаем только когда посылка нашлась в СДЭК по трек-номеру.
        return data.deliveryFromCdek && date
          ? { label: t('progress_delivery_pin_cdek'), value: date }
          : { label: t('progress_delivery_pin') };
      default:
        return null;
    }
  };

  return (
    <section className="mg-section mg-dash__progress">
      <div className="mg-label">{t('progress_eyebrow')}</div>

      <ol className="mg-prg">
        {data.stages.map((stage, i) => {
          const pin = pinFor(stage);
          const isLast = i === data.stages.length - 1;
          // Отрезок вниз красим по пройденности. Постоплата — не этап
          // производства: если книга уже уехала дальше, линия под ней
          // должна остаться сплошной, иначе в середине пройденного пути
          // появляется серый разрыв.
          const lineDone =
            stage.state === 'done' ||
            (stage.key === 'postpayment' && data.stages[i + 1]?.state === 'done');
          const hasHint = HINT_STAGES.includes(stage.key);
          const hintOpen = openHint === stage.key;
          return (
            <li
              key={stage.key}
              className={`mg-prg__item mg-prg__item--${stage.state}`}
            >
              <div className="mg-prg__rail">
                <span className="mg-prg__dot">
                  {stage.state === 'done' && <CheckIcon />}
                </span>
                {!isLast && (
                  <span
                    className={`mg-prg__line${lineDone ? ' mg-prg__line--done' : ''}`}
                  />
                )}
              </div>

              <div className="mg-prg__body">
                <h3 className="mg-prg__title">{t(`progress_${stage.key}_title`)}</h3>
                <p className="mg-prg__text">{t(`progress_${stage.key}_text`)}</p>
                {pin && (
                  <PinRow
                    label={pin.label}
                    value={pin.value}
                    hint={hasHint ? (t(`progress_${stage.key}_hint`) as string) : undefined}
                    open={hintOpen}
                    onToggle={() => setOpenHint(hintOpen ? null : stage.key)}
                    stageKey={stage.key}
                    pinSize={pinSize}
                    onPinMeasure={handlePinMeasure}
                  />
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
};

export default BookProgress;
